import { isSupported, onMessage } from "firebase/messaging";
import i18next from "i18next";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import AlertHelper from "~helpers/AlertHelper";
import DialogHelper from "~helpers/DialogHelper";
import NotificationHelper from "~helpers/NotificationHelper";
import { getCurrentUser } from "~store/actions";
import { updateCurrentDevice } from "~store/device/actions";
import { selectUserInfo } from "~store/user/selectors";

import { setIsNotificationRequesting } from "./actions";
import * as at from "./actionTypes";

const waitLoad = () =>
  new Promise((resolve) => {
    if (document.readyState === "complete") {
      resolve();
      return;
    }
    const loadListener = () => {
      resolve();
      window.removeEventListener("load", loadListener);
    };
    window.addEventListener("load", loadListener);
  });

const waitForActiveServiceWorker = (registration, timeout = 10_000) => {
  return new Promise((resolve, reject) => {
    if (registration.active) {
      resolve(registration);
      return;
    }

    const startTime = Date.now();

    const checkActive = () => {
      if (registration.active) {
        resolve(registration);
      } else if (Date.now() - startTime >= timeout) {
        reject(new Error("Service worker activation timed out"));
      } else {
        setTimeout(checkActive, 500);
      }
    };

    checkActive();
  });
};

const registerFirebaseOnMessageListener = async (callback) => {
  const supported = await isSupported();

  if (!supported) {
    console.warn("Firebase messaging is not supported in this browser.");
    return () => {};
  }

  try {
    const messaging = NotificationHelper.getMessagingInstance();

    const unsubscribe = onMessage(messaging, (payload) => {
      callback(payload);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  } catch (err) {
    console.error("Firebase onMessage listener error:", err);
    return () => {};
  }
};

let firebaseForegroundListenerCleanup;
function* handleRequestPushNotification({ payload }) {
  const silentRegisterAndEnable = payload || false;

  let permissionState = null;

  if ("permissions" in navigator) {
    const status = yield call(navigator.permissions.query.bind(navigator.permissions), { name: "notifications" });
    permissionState = status.state;

    if (permissionState === "denied") {
      console.log("Notification permission is denied.");
      if (!silentRegisterAndEnable) {
        DialogHelper.showOk(
          i18next.t("common.notification.permissionDeniedTitle"),
          i18next.t("common.notification.permissionDeniedBody")
        );
      }
      return;
    }
  }

  if (silentRegisterAndEnable && Notification.permission !== "granted") {
    console.log("Notification permission is not granted.");
    return;
  }

  const vapidKey = window.pr_config?.firebase;
  if (!vapidKey) return console.log("Vapid key is not found");
  const firebaseRegistration = yield call(NotificationHelper.getRegistration);
  const isFirebaseWorkerExistAndActive = !!firebaseRegistration && !!firebaseRegistration.active;
  const userInfo = yield select(selectUserInfo);

  if (isFirebaseWorkerExistAndActive && userInfo?.device?.registration_id) {
    if (firebaseForegroundListenerCleanup) {
      firebaseForegroundListenerCleanup.then((cleanup) => {
        cleanup?.();
      });
    }
    firebaseForegroundListenerCleanup = registerFirebaseOnMessageListener((payload) => {
      const { test } = payload.data;
      if (!test) return;
      const { title, body } = payload.notification;
      const notificationOptions = {
        body,
        icon: "/logo192.png",
        data: payload.data,
      };
      const notification = new Notification(title, notificationOptions);
      notification.onclick = (event) => {
        event.preventDefault();
      };
    });
  }
  if (isFirebaseWorkerExistAndActive && !userInfo?.id) {
    console.log("User is not logged in, skipping token update.");
    return;
  } else if (isFirebaseWorkerExistAndActive && userInfo.device?.registration_id) {
    console.log("Push notification services are registered and running.🚀");
    // Call it on initialization step for work properly for deleteToken call. https://github.com/firebase/firebase-js-sdk/issues/8621#issuecomment-2452454877
    NotificationHelper.getFirebaseToken(vapidKey);
    return;
  } else if (!userInfo.device?.id) {
    console.log("User device id is not found, skipping notification registration.");
    return;
  }

  if (!("serviceWorker" in navigator) || !("Notification" in window)) {
    console.log("Service worker or Notification API not supported");
    if (!silentRegisterAndEnable) {
      AlertHelper.showError(i18next.t("common.notification.workerNotSupported"));
    }
    return;
  }

  yield put(setIsNotificationRequesting(true));
  try {
    yield call(waitLoad);

    const permissionResult = yield call(Notification.requestPermission);

    if (permissionResult === "granted") {
      try {
        const tryRegisterAndGetToken = async () => {
          const registration = await NotificationHelper.registerWorker();
          await waitForActiveServiceWorker(registration, 10_000);
          const token = await NotificationHelper.getFirebaseToken(vapidKey);
          return token;
        };

        let token = null;
        //try twice to get token
        try {
          token = yield call(tryRegisterAndGetToken);
        } catch (err) {
          console.log("Error getting token, trying again...", err);

          yield new Promise((resolve) => setTimeout(resolve, 5000));
          token = yield call(tryRegisterAndGetToken);
        }
        if (token) {
          console.log("FCM Token:", token);

          yield put(
            updateCurrentDevice(
              {
                registration_id: token,
                push_platform: "APNS",
              },
              {
                loading: !silentRegisterAndEnable,
                noAlert: !silentRegisterAndEnable,
              }
            )
          );
          if (!silentRegisterAndEnable) {
            AlertHelper.showSuccess(i18next.t("common.notification.permissionGranted"));
          }
          yield put(
            getCurrentUser({
              loading: false,
              noAlert: true,
            })
          );
        }
      } catch (err) {
        console.log("An error occurred while retrieving token. ", err);
        if (!silentRegisterAndEnable) {
          AlertHelper.showError(i18next.t("common.notification.permissionError"));
        }
      }
    } else {
      console.log("Unable to get permission to notify.");
      if (!silentRegisterAndEnable) {
        AlertHelper.showError(i18next.t("common.notification.permissionRejected"));
      }
    }
  } catch (error) {
    console.log("Service worker registration failed, error:", error);
    if (!silentRegisterAndEnable) {
      AlertHelper.showError(i18next.t("common.notification.permissionError2"));
    }

    yield put(setIsNotificationRequesting(false));
  } finally {
    yield put(setIsNotificationRequesting(false));
  }
}

function* handleClearPushNotification({ payload }) {
  try {
    yield call(NotificationHelper.deleteFirebaseToken);
  } catch (error) {
    console.error("Error deleting FCM token:", error);
  }
  try {
    yield call(NotificationHelper.unregisterWorker);
  } catch (error) {
    console.error("Error unregistering service workers:", error);
  }
}
function* handlePushNotification({ payload }) {}
function* handlePushNotificationAction({ payload }) {}

function* scenarioManagerSaga() {
  yield all([
    takeLatest(at.REQUEST_PUSH_NOTIFICATION, handleRequestPushNotification),
    takeLatest(at.CLEAR_PUSH_NOTIFICATION, handleClearPushNotification),
    takeLatest(at.PUSH_NOTIFICATION, handlePushNotification),
    takeLatest(at.PUSH_NOTIFICATION_ACTION, handlePushNotificationAction),
  ]);
}

export default scenarioManagerSaga;
